import React from "react";
import { AlertTriangle, Clock, FolderOpen, Plus, Users } from "lucide-react";
import { Project } from "@amzn/afxtaco-types";
import { formatAbsoluteDate, formatRelativeTime } from "@/utils/formatTime";
import StatCard from "./StatCard";
import {
  hasCustomerContext,
  readCustomerContext,
} from "@/utils/customerContext";

interface StatusStripProps {
  project: Project | undefined;
  /**
   * When TACO last ran a turn — `Project.latestSessionLastActivityAt`, which the
   * orchestrator stamps at both turn start and turn completion.
   *
   * Deliberately not `updatedAt`: that is stamped on any write to the project
   * row, so it both misses turns (a scoping turn changes nothing on the row, and
   * the version adapter skips the write entirely) and counts writes that were not
   * TACO at all, like a rename or saving customer IDs.
   */
  lastActivityAt: string | undefined;
  /** Number of generated deliverables, or undefined while still loading. */
  deliverableCount: number | undefined;
  /** How many deliverables changed in the last 24h. */
  changedTodayCount: number | undefined;
  /** Opens the customer dialog, to set the IDs or correct them. */
  onEditCustomer: () => void;
}

/**
 * The three facts at the top of the Overview: who the project is for, when it
 * last moved, and how much has been produced.
 *
 * Customer leads deliberately — it is what ties the project to the deal, and the
 * agent asks for these IDs when they're missing, so the unlinked state doubles
 * as the nudge.
 *
 * Two boxes were considered and rejected: version count (an internal counter the
 * user can't act on, with no version picker to navigate to, and not monotonic)
 * and session expiry (24h from session creation, never refreshed by activity,
 * and harmless when it lapses — the transcript and every asset survive).
 */
const StatusStrip: React.FC<StatusStripProps> = ({
  project,
  lastActivityAt,
  deliverableCount,
  changedTodayCount,
  onEditCustomer,
}) => {
  // Real data: the IDs are recorded on the project record — by the user via the
  // start-project form or the Customer box, or by the agent's `associate_entities`
  // tool — so none of this is inferred from chat.
  const customer = readCustomerContext(project);
  const isLinked = hasCustomerContext(customer);

  // Until the project record arrives we do not know whether a customer is linked.
  // Saying "Not linked" here would be a false statement rather than an empty one,
  // which is what made a just-created project look like the IDs hadn't saved.
  const isProjectKnown = project !== undefined;

  // Three equal columns spanning the full width, stacking on narrow viewports.
  // A grid rather than flex-wrap so the boxes stay equal regardless of content
  // length — the Customer box holds two 18-char IDs and would otherwise grow and
  // knock the other two out of alignment.
  return (
    <div className="grid grid-cols-1 gap-2.5 mb-5 sm:grid-cols-3">
      <StatCard icon={<Users className="h-3 w-3" />} label="Customer">
        {!isProjectKnown ? (
          <div className="text-sm font-medium text-muted-foreground">—</div>
        ) : isLinked ? (
          <>
            {customer.accountIds.length > 0 && (
              <div className="truncate font-mono text-[13px] font-semibold text-foreground">
                {customer.accountIds[0]}
                {customer.accountIds.length > 1 && (
                  <span className="ml-1 font-sans text-[11px] font-normal text-muted-foreground">
                    +{customer.accountIds.length - 1} more
                  </span>
                )}
              </div>
            )}
            {customer.opportunityIds.length > 0 && (
              <div className="mt-px truncate font-mono text-[11px] text-muted-foreground">
                Opp {customer.opportunityIds[0]}
                {customer.opportunityIds.length > 1 && (
                  <span className="ml-1 font-sans">
                    +{customer.opportunityIds.length - 1} more
                  </span>
                )}
              </div>
            )}
            {/*
              "Change", not "Link another": a project has one customer. This is
              how you correct a typo or point the project at a different account,
              and submitting replaces the ID rather than accumulating a second.
              Multiple IDs can still arrive from the agent's `associate_entities`,
              so the display above tolerates them — it just doesn't invite them.
            */}
            <button
              type="button"
              onClick={onEditCustomer}
              className="mt-1 text-[11px] font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              Change
            </button>
          </>
        ) : (
          <>
            {/*
              Amber, and only once the record has loaded, so it marks a real gap
              rather than a slow fetch. Deliberately not red and carrying no error
              copy: nothing has gone wrong, but an ungrounded design is materially
              weaker, so this is the one place worth nudging.

              Decorative — "Not linked" beside it already states the condition, so
              the glyph is hidden from screen readers instead of repeating it.
            */}
            <div className="flex items-center gap-1.5">
              <AlertTriangle
                className="h-3.5 w-3.5 flex-none text-amber-600 dark:text-amber-400"
                aria-hidden="true"
              />
              <div className="text-sm font-medium text-muted-foreground">
                Not linked
              </div>
            </div>
            <button
              type="button"
              onClick={onEditCustomer}
              className="mt-0.5 inline-flex items-center gap-1 text-[11.5px] font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              Add an account to ground the design
              <Plus className="h-3 w-3" />
            </button>
          </>
        )}
      </StatCard>

      <StatCard
        icon={<Clock className="h-3 w-3" />}
        label="Last activity"
        // Three distinct states rather than one formatter call: "—" while the
        // record is in flight (we don't know yet), explicit copy when TACO
        // genuinely has not run, and the relative time otherwise. Passing an
        // absent timestamp straight to the formatter would render "Unknown",
        // which conflates "still loading" with "never happened".
        value={
          !isProjectKnown
            ? "—"
            : lastActivityAt
              ? formatRelativeTime(lastActivityAt)
              : "No turns yet"
        }
        sub={
          project?.createdAt
            ? `Created ${formatAbsoluteDate(project.createdAt)}`
            : undefined
        }
      />

      <StatCard
        icon={<FolderOpen className="h-3 w-3" />}
        label="Deliverables"
        value={
          deliverableCount === undefined
            ? "—"
            : `${deliverableCount} file${deliverableCount === 1 ? "" : "s"}`
        }
        sub={
          changedTodayCount === undefined
            ? undefined
            : changedTodayCount > 0
              ? `${changedTodayCount} changed today`
              : "None changed today"
        }
      />
    </div>
  );
};

export default StatusStrip;
